import { ReactElement } from 'react'
// @ts-ignore
import currencyFormatter from 'currency-formatter'
import { EventDebits } from '../../types/event-debits'
import {
  Card,
  StatementRow,
  StatementLabel,
  StatementValue,
  StatementTotal,
  Breakdown,
  BreakdownList,
  CoverageHead,
  CoverageBar,
  CoverageFill,
  CoverageNote,
} from './styles'

interface EventDebitsCardProps {
  debits: EventDebits
  availableBalance: number
}

const toBRL = (value: number): string =>
  currencyFormatter.format(value, { code: 'BRL' })

export function EventDebitsCard({
  debits,
  availableBalance,
}: EventDebitsCardProps): ReactElement | null {
  if (debits === undefined || debits === null || debits.remaining <= 0) {
    return null
  }

  const percentage =
    debits.totalToCover > 0
      ? Math.min((debits.covered / debits.totalToCover) * 100, 100)
      : 0

  const { breakdown } = debits

  return (
    <Card>
      <StatementRow>
        <StatementLabel>Recebimentos disponíveis até hoje</StatementLabel>
        <StatementValue>{toBRL(debits.grossReceived)}</StatementValue>
      </StatementRow>
      <StatementRow>
        <StatementLabel>
          Débitos do evento
          <small>serviços, taxas e ajustes do fechamento</small>
        </StatementLabel>
        <StatementValue negative>
          − {toBRL(debits.totalToCover)}
        </StatementValue>
      </StatementRow>
      <Breakdown>
        <summary>Ver detalhamento dos débitos</summary>
        <BreakdownList>
          <StatementRow>
            <StatementLabel>Serviços</StatementLabel>
            <StatementValue>{toBRL(breakdown.services)}</StatementValue>
          </StatementRow>
          <StatementRow>
            <StatementLabel>Outros débitos</StatementLabel>
            <StatementValue>{toBRL(breakdown.otherDebits)}</StatementValue>
          </StatementRow>
          <StatementRow>
            <StatementLabel>Ajustes de caixa e descontos</StatementLabel>
            <StatementValue negative={breakdown.credits < 0}>
              {toBRL(breakdown.credits)}
            </StatementValue>
          </StatementRow>
          <StatementRow>
            <StatementLabel>Taxa de antecipação estornada</StatementLabel>
            <StatementValue>
              {toBRL(breakdown.advanceFeeReversal)}
            </StatementValue>
          </StatementRow>
          <StatementRow>
            <StatementLabel>Saques de outros créditos</StatementLabel>
            <StatementValue>
              {toBRL(breakdown.otherCreditsWithdrawals)}
            </StatementValue>
          </StatementRow>
        </BreakdownList>
      </Breakdown>
      <StatementTotal>
        <span>Saldo disponível para saque</span>
        <span>{toBRL(availableBalance)}</span>
      </StatementTotal>
      <CoverageHead>
        <strong>Cobertura dos débitos</strong>
        <span>
          {toBRL(debits.covered)} de {toBRL(debits.totalToCover)} (
          {Math.floor(percentage)}%)
        </span>
      </CoverageHead>
      <CoverageBar>
        <CoverageFill percentage={percentage} />
      </CoverageBar>
      <CoverageNote>
        Seus recebimentos são usados primeiro para cobrir os débitos do evento.
        Faltam <b>{toBRL(debits.remaining)}</b> — novos recebimentos e
        antecipações abatem esse valor até o saldo começar a ser liberado.
      </CoverageNote>
    </Card>
  )
}
